import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Modal,
  IconButton,
} from "@mui/material";
import { Field, Form } from "react-final-form";
import axios from "axios";
import { toast } from "react-toastify";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import SmartDisplayOutlinedIcon from "@mui/icons-material/SmartDisplayOutlined";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import MusicNoteOutlinedIcon from "@mui/icons-material/MusicNoteOutlined";
import styles from "./PlaylistModal.module.scss";

const API_KEY = "AIzaSyCKfUSr5PacGOhWURYgZlgjmgGh4Oc5kjE";

type VideoItem = {
  id?: number | string;
  title?: string;
  description?: string;
  url: string;
  thumbnail?: string;
  platform?: string;
  duration?: string;
  type?: string;
};

type CurrentCategory = {
  videos: VideoItem[];
};

type PlaylistModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: any) => void;
  playlistId: number | string;
  currentCategory: CurrentCategory;
};

type VideoDetails = {
  title: string;
  description: string;
  thumbnail: string;
  duration: string;
};

const initialVideoDetails: VideoDetails = {
  title: "",
  description: "",
  thumbnail: "",
  duration: "",
};

const isValidYouTubeUrl = (url: string) => {
  const pattern =
    /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})\b/;
  return pattern.test(url);
};

const isValidTikTokUrl = (url: string) => {
  try {
    const parsed = new URL(url.trim());
    const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
    const isTikTokHost =
      host === "tiktok.com" || host.endsWith(".tiktok.com");
    const isVideoPath = /\/video\/\d+\/?$/i.test(parsed.pathname);
    const isShortLink = host === "vm.tiktok.com" || host === "vt.tiktok.com";

    return parsed.protocol.startsWith("http") && isTikTokHost && (isVideoPath || isShortLink);
  } catch {
    return false;
  }
};

const parseDuration = (isoDuration: string) => {
  const match = isoDuration.match(
    /PT(?:([\d]+)H)?(?:([\d]+)M)?(?:([\d]+)S)?/
  );

  if (!match) return "00:00:00";

  const hours = parseInt(match[1] || "0", 10);
  const minutes = parseInt(match[2] || "0", 10);
  const seconds = parseInt(match[3] || "0", 10);

  const formattedHours = hours.toString().padStart(2, "0");
  const formattedMinutes = minutes.toString().padStart(2, "0");
  const formattedSeconds = seconds.toString().padStart(2, "0");

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

const extractVideoId = (url: string) => {
  const regex =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

const hasDuplicateYouTubeVideo = (videos: VideoItem[], url: string) => {
  const videoId = extractVideoId(url);
  if (!videoId) return false;

  return videos.some((video) => extractVideoId(video.url) === videoId);
};

const normalizeUrl = (value: string) =>
  value.trim().replace(/\/$/, "").toLowerCase();

const getVideoDetails = async (videoId: string) => {
  const url = `https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails&id=${videoId}&key=${API_KEY}`;

  try {
    const response = await fetch(url);
    const data = await response.json();

    if (data.items?.length > 0) {
      const videoData = data.items[0];
      return {
        title: videoData.snippet?.title || "",
        description: videoData.snippet?.description || "",
        thumbnail: videoData.snippet?.thumbnails?.high?.url || "",
        duration: parseDuration(videoData.contentDetails?.duration || "PT0S"),
      };
    }

    throw new Error("Video not found");
  } catch (error) {
    console.error("Error fetching video details:", error);
    return null;
  }
};

const PlaylistModal: React.FC<PlaylistModalProps> = ({
  open,
  onClose,
  onSubmit,
  playlistId,
  currentCategory,
}) => {
  const initialFormValues = {
    title: "",
    description: "",
    url: "",
    tiktokCopyUrl: "",
    instagramReelUrl: "",
    platform: "youtube",
  };

  const [formValues, setFormValues] = useState(initialFormValues);
  const [videoDetails, setVideoDetails] =
    useState<VideoDetails>(initialVideoDetails);
  const [s3Videos, setS3Videos] = useState<VideoItem[]>([]);
  const [hoveredVideo, setHoveredVideo] = useState<string | null>(null);
  const [selectedVideos, setSelectedVideos] = useState<string[]>([]);
  const [url, setUrl] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTikTokLoading, setIsTikTokLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalVideoId, setModalVideoId] = useState<string | null>(null);
  const tiktokLookupTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      resetLocalState();
    }
  }, [open]);

  useEffect(
    () => () => {
      if (tiktokLookupTimerRef.current !== null) {
        window.clearTimeout(tiktokLookupTimerRef.current);
      }
    },
    []
  );

  const resetLocalState = () => {
    if (tiktokLookupTimerRef.current !== null) {
      window.clearTimeout(tiktokLookupTimerRef.current);
      tiktokLookupTimerRef.current = null;
    }

    setFormValues(initialFormValues);
    setVideoDetails(initialVideoDetails);
    setS3Videos([]);
    setHoveredVideo(null);
    setSelectedVideos([]);
    setUrl("");
    setSearchQuery("");
    setSearchResults([]);
    setNextPageToken(null);
    setShowResults(false);
    setIsLoading(false);
    setIsTikTokLoading(false);
    setModalOpen(false);
    setModalVideoId(null);
  };

  const resetAndClose = (form?: any) => {
    if (form) {
      form.reset();
      form.change("url", "");
      form.change("title", "");
      form.change("description", "");
      form.change("tiktokCopyUrl", "");
      form.change("instagramReelUrl", "");
      form.change("platform", "youtube");
    }

    resetLocalState();
    onClose();
  };

  const validate = (values: any) => {
    const errors: Record<string, string> = {};

    if (values.platform === "youtube") {
      if (!values.url?.trim()) {
        errors.url = "Required";
      } else if (!isValidYouTubeUrl(values.url.trim())) {
        errors.url = "Invalid YouTube URL";
      } else if (
        hasDuplicateYouTubeVideo(
          currentCategory?.videos || [],
          values.url.trim()
        )
      ) {
        errors.url = "This video is already in the playlist";
      }

      if (!values.title?.trim()) {
        errors.title = "Required";
      }
    }

    if (values.platform === "tk") {
      if (!values.tiktokCopyUrl?.trim()) {
        errors.tiktokCopyUrl = "Required";
      } else if (!isValidTikTokUrl(values.tiktokCopyUrl.trim())) {
        errors.tiktokCopyUrl = "Invalid TikTok URL";
      } else if (
        (currentCategory?.videos || []).some(
          (video) =>
            normalizeUrl(video.url) === normalizeUrl(values.tiktokCopyUrl)
        )
      ) {
        errors.tiktokCopyUrl = "This video is already in the playlist";
      }
    }

    return errors;
  };

  const handleScrubVideoDetails = async (youtubeUrl: string, form: any) => {
    const videoId = extractVideoId(youtubeUrl);

    if (!videoId) {
      toast.error("Invalid YouTube URL.");
      return;
    }

    const details = await getVideoDetails(videoId);

    if (!details) {
      toast.error("Failed to fetch video details.");
      return;
    }

    setVideoDetails(details);
    form.change("title", details.title);
    form.change("description", details.description);
    form.change("url", youtubeUrl);

    setFormValues((prev) => ({
      ...prev,
      url: youtubeUrl,
      title: details.title,
      description: details.description,
    }));
  };

  const handleScrubTikTokDetails = async (tkUrl: string, form: any) => {
    try {
      const res = await fetch(
        `https://www.tiktok.com/oembed?url=${encodeURIComponent(tkUrl)}`
      );
      if (!res.ok) {
        throw new Error(`TikTok metadata request failed with ${res.status}`);
      }
      const data = await res.json();

      const currentValues = form.getState().values;
      if (
        currentValues.platform !== "tk" ||
        currentValues.tiktokCopyUrl?.trim() !== tkUrl
      ) {
        return;
      }

      const updatedDetails: VideoDetails = {
        title: data.title || "TikTok video",
        thumbnail: data.thumbnail_url || "",
        description: data.author_name ? `By ${data.author_name}` : "",
        duration: "00:00:00",
      };

      setVideoDetails(updatedDetails);

      form.change("title", updatedDetails.title);
      form.change("description", updatedDetails.description);
      form.change("tiktokCopyUrl", tkUrl);

      setFormValues((prev) => ({
        ...prev,
        platform: "tk",
        title: updatedDetails.title,
        description: updatedDetails.description,
        tiktokCopyUrl: tkUrl,
      }));
    } catch (error) {
      console.error("Failed to get TikTok details:", error);
      const currentValues = form.getState().values;
      if (
        currentValues.platform !== "tk" ||
        currentValues.tiktokCopyUrl?.trim() !== tkUrl
      ) {
        return;
      }

      const fallbackDetails = {
        ...initialVideoDetails,
        title: "TikTok video",
      };
      setVideoDetails(fallbackDetails);
      form.change("title", fallbackDetails.title);
      form.change("tiktokCopyUrl", tkUrl);
    }
  };

  const scheduleTikTokDetails = (value: string, form: any) => {
    if (tiktokLookupTimerRef.current !== null) {
      window.clearTimeout(tiktokLookupTimerRef.current);
      tiktokLookupTimerRef.current = null;
    }

    const trimmedValue = value.trim();
    if (!isValidTikTokUrl(trimmedValue)) {
      setIsTikTokLoading(false);
      return;
    }

    setIsTikTokLoading(true);
    tiktokLookupTimerRef.current = window.setTimeout(async () => {
      tiktokLookupTimerRef.current = null;
      try {
        await handleScrubTikTokDetails(trimmedValue, form);
      } finally {
        setIsTikTokLoading(false);
      }
    }, 1000);
  };

  const getS3Data = async () => {
    try {
      const response = await axios.get(
        `/playlist/global/get_global_playlist_items`
      );
      setS3Videos(response.data || []);
    } catch (error) {
      console.error("Error fetching S3 playlist items:", error);
      toast.error("Failed to fetch stock videos.");
    }
  };

  const toggleSelection = (videoUrl: string) => {
    setSelectedVideos((prevSelected) =>
      prevSelected.includes(videoUrl)
        ? prevSelected.filter((item) => item !== videoUrl)
        : [...prevSelected, videoUrl]
    );
  };

  const handleMouseEnter = (video: VideoItem) => {
    setHoveredVideo(video.url);
  };

  const handleMouseLeave = () => {
    setHoveredVideo(null);
  };

  const searchYouTubeVideos = async (
    query: string,
    pageToken: string | null = null
  ) => {
    const baseUrl = `https://www.googleapis.com/youtube/v3/search?part=snippet&type=video&maxResults=20&q=${encodeURIComponent(
      query
    )}&key=${API_KEY}`;

    const requestUrl = pageToken ? `${baseUrl}&pageToken=${pageToken}` : baseUrl;

    try {
      const response = await fetch(requestUrl);
      const data = await response.json();

      return {
        items: data.items || [],
        nextPageToken: data.nextPageToken || null,
      };
    } catch (error) {
      console.error("YouTube Search Error:", error);
      return {
        items: [],
        nextPageToken: null,
      };
    }
  };

  const selectYouTubeVideo = async (videoId: string, form: any) => {
    const fullUrl = `https://www.youtube.com/watch?v=${videoId}`;
    setUrl(fullUrl);
    setSearchQuery(fullUrl);

    const details = await getVideoDetails(videoId);

    if (!details) {
      toast.error("Failed to fetch video details.");
      return;
    }

    setVideoDetails(details);
    form.change("url", fullUrl);
    form.change("title", details.title);
    form.change("description", details.description);

    setFormValues((prev) => ({
      ...prev,
      url: fullUrl,
      title: details.title,
      description: details.description,
    }));

    setShowResults(false);
  };

  const handleSearch = async (form?: any) => {
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) return;

    const pastedVideoId = extractVideoId(trimmedQuery);
    if (pastedVideoId && form) {
      await selectYouTubeVideo(pastedVideoId, form);
      return;
    }

    setIsLoading(true);
    const { items, nextPageToken: token } = await searchYouTubeVideos(trimmedQuery);
    setSearchResults(items);
    setNextPageToken(token);
    setShowResults(true);
    setIsLoading(false);
  };

  const handleLoadMore = async () => {
    if (!nextPageToken) return;

    setIsLoading(true);
    const { items, nextPageToken: token } = await searchYouTubeVideos(
      searchQuery.trim(),
      nextPageToken
    );
    setSearchResults((prev) => [...prev, ...items]);
    setNextPageToken(token);
    setIsLoading(false);
  };

  const buildExistingVideosPayload = () => {
    return (currentCategory?.videos || []).map(({ id, ...video }) => ({
      title: video.title || "",
      description: video.description || "",
      url: video.url,
      thumbnail: video.thumbnail || "",
      platform: video.platform || "s3",
      duration: video.duration || "00:00:00",
      type: video.type || "mp4",
    }));
  };

  const handleSubmitYouTube = async (values: any, form: any) => {
    const videoId = extractVideoId(values.url);

    if (!videoId) {
      toast.error("Invalid YouTube URL.");
      return;
    }

    if (!values.title?.trim()) {
      form.change("title", values.title || "");
      toast.error("Title is required.");
      return;
    }

    if (hasDuplicateYouTubeVideo(currentCategory?.videos || [], values.url)) {
      toast.error("This YouTube video is already in the playlist.");
      return;
    }

    try {
      const newVideo = {
        title: values.title.trim(),
        description: values.description || videoDetails.description || "",
        url: values.url.trim(),
        thumbnail: videoDetails.thumbnail || "",
        platform: values.platform || "youtube",
        duration: videoDetails.duration || "00:00:00",
        type: "mp4",
      };

      const updatedVideos = buildExistingVideosPayload();
      updatedVideos.push(newVideo);

      const response = await axios.put(
        `playlist/${playlistId}/save_playlist_items`,
        updatedVideos
      );

      if (response.status === 200 && response.data === true) {
        toast.success("Playlist item saved successfully");
        onSubmit(newVideo);
        resetAndClose(form);
      } else {
        toast.error("Failed to save playlist item.");
      }
    } catch (error) {
      console.error("Error saving playlist item:", error);
      toast.error("Failed to save playlist item.");
    }
  };

  const handleSubmitS3 = async (_values: any, form: any) => {
    if (selectedVideos.length === 0) {
      toast.error("Please select at least one stock video.");
      return;
    }

    const selectedVideoData = s3Videos
      .filter((video) => selectedVideos.includes(video.url))
      .map(({ id, ...video }) => ({
        title: video.title || "",
        description: video.description || "",
        url: video.url,
        thumbnail: video.thumbnail || "",
        platform: video.platform || "s3",
        duration: video.duration || "00:00:00",
        type: "mp4",
      }));

    const updatedVideos = buildExistingVideosPayload();
    const playlistPayload = [...updatedVideos, ...selectedVideoData];

    try {
      const response = await axios.put(
        `playlist/${playlistId}/save_playlist_items`,
        playlistPayload
      );

      if (response.status === 200 && response.data === true) {
        toast.success("Playlist item saved successfully");
        selectedVideoData.forEach((video) => onSubmit(video));
        resetAndClose(form);
      } else {
        toast.error("Failed to save playlist item.");
      }
    } catch (error) {
      console.error("Error saving playlist item:", error);
      toast.error("Failed to save playlist item.");
    }
  };

  const handleSubmitTikTok = async (values: any, form: any) => {
    const tkUrl = values.tiktokCopyUrl?.trim();

    if (!tkUrl) {
      form.change("tiktokCopyUrl", "");
      toast.error("TikTok URL is required.");
      return;
    }

    if (!isValidTikTokUrl(tkUrl)) {
      toast.error("Invalid TikTok URL.");
      return;
    }

    if (
      (currentCategory?.videos || []).some(
        (video) => normalizeUrl(video.url) === normalizeUrl(tkUrl)
      )
    ) {
      toast.error("This TikTok video is already in the playlist.");
      return;
    }

    const title = values.title?.trim() || videoDetails.title || "TikTok video";

    try {
      const newVideo = {
        title,
        description: values.description || videoDetails.description || "",
        url: tkUrl,
        platform: "tiktok",
        duration: videoDetails.duration || "00:00:00",
        type: "mp4",
        thumbnail: videoDetails.thumbnail || "",
      };

      const existingVideos = buildExistingVideosPayload();
      const payload = [...existingVideos, newVideo];

      const response = await axios.put(
        `/playlist/${playlistId}/save_playlist_items`,
        payload
      );

      if (response.status === 200 && response.data === true) {
        toast.success("TikTok video added to playlist successfully");
        onSubmit(newVideo);
        resetAndClose(form);
      } else {
        toast.error("Failed to add TikTok video to playlist.");
      }
    } catch (error) {
      console.error("Error adding TikTok to playlist:", error);
      toast.error("Failed to add TikTok video to playlist.");
    }
  };

  const handlePlatformChange = (platform: "youtube" | "S3" | "tk", form: any) => {
    if (tiktokLookupTimerRef.current !== null) {
      window.clearTimeout(tiktokLookupTimerRef.current);
      tiktokLookupTimerRef.current = null;
    }

    form.change("platform", platform);
    setFormValues({ ...initialFormValues, platform });

    setVideoDetails(initialVideoDetails);
    setIsTikTokLoading(false);
    setUrl("");
    setSearchQuery("");
    setSearchResults([]);
    setShowResults(false);
    setSelectedVideos([]);
    setHoveredVideo(null);

    form.change("url", "");
    form.change("title", "");
    form.change("description", "");
    form.change("tiktokCopyUrl", "");
    form.change("instagramReelUrl", "");

    if (platform === "S3") {
      getS3Data();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          onClose();
        }
      }}
      PaperProps={{
        className: styles.modalPaper,
      }}
    >
      <div className={styles.modalHeader}>
        <div className={styles.modalTitleWrap}>
          <h2 className={styles.modalTitle}>Add Video</h2>
          <p className={styles.modalSubtitle}>
            Paste a video URL from any supported platform.
          </p>
        </div>

        <IconButton
          className={styles.closeButton}
          onClick={() => {
            resetLocalState();
            onClose();
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <DialogContent className={styles.modalContent}>
        <Form
          onSubmit={async (values, form) => {
            if (values.platform === "S3") {
              await handleSubmitS3(values, form);
              return;
            }

            if (values.platform === "youtube") {
              await handleSubmitYouTube(values, form);
              return;
            }

            if (values.platform === "tk") {
              await handleSubmitTikTok(values, form);
            }
          }}
          validate={validate}
          initialValues={formValues}
          render={({ handleSubmit, form, values }) => (
            <form
              className={styles.form}
              onSubmit={async (e) => {
                e.preventDefault();
                await handleSubmit(e);
              }}
            >
              <Field name="platform">
                {({ input }) => (
                  <>
                    <input type="hidden" {...input} />

                    <div className={styles.platformTabs}>
                      <button
                        type="button"
                        className={`${styles.platformTab} ${
                          values.platform === "youtube"
                            ? styles.platformTabActive
                            : ""
                        }`}
                        onClick={() => handlePlatformChange("youtube", form)}
                      >
                        <SmartDisplayOutlinedIcon className={styles.platformTabIcon} />
                        <span>YouTube</span>
                      </button>

                      <button
                        type="button"
                        className={`${styles.platformTab} ${
                          values.platform === "S3"
                            ? styles.platformTabActive
                            : ""
                        }`}
                        onClick={() => handlePlatformChange("S3", form)}
                      >
                        <VideoLibraryOutlinedIcon className={styles.platformTabIcon} />
                        <span>Stock</span>
                      </button>

                      <button
                        type="button"
                        className={`${styles.platformTab} ${
                          values.platform === "tk" ? styles.platformTabActive : ""
                        }`}
                        onClick={() => handlePlatformChange("tk", form)}
                      >
                        <MusicNoteOutlinedIcon className={styles.platformTabIcon} />
                        <span>TikTok</span>
                      </button>
                    </div>
                  </>
                )}
              </Field>

              {values.platform === "youtube" && (
                <div className={styles.fieldCard}>
                  <div className={styles.searchRow}>
                    <TextField
                      className={styles.searchInput}
                      label="Search YouTube Videos"
                      fullWidth
                      size="small"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onPaste={(e) => {
                        const pastedText = e.clipboardData.getData("text").trim();
                        if (!pastedText) return;

                        setTimeout(async () => {
                          setSearchQuery(pastedText);
                          const pastedVideoId = extractVideoId(pastedText);

                          if (pastedVideoId) {
                            await selectYouTubeVideo(pastedVideoId, form);
                          }
                        }, 0);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSearch(form);
                        }
                      }}
                    />

                    <Button
                      className={styles.secondaryActionButton}
                      variant="outlined"
                      onClick={() => handleSearch(form)}
                      disabled={isLoading || !searchQuery.trim()}
                    >
                      {isLoading ? "Searching..." : "Search"}
                    </Button>
                  </div>

                  {showResults && searchResults.length > 0 && (
                    <div className={styles.resultsViewport}>
                      <div className={styles.resultsGrid}>
                        {searchResults.map((video) => {
                          const videoId = video.id.videoId;
                          const title = video.snippet.title;
                          const thumbnail = video.snippet.thumbnails.medium.url;

                          return (
                            <Card
                              key={videoId}
                              className={styles.videoCard}
                              onClick={() => selectYouTubeVideo(videoId, form)}
                            >
                              <IconButton
                                aria-label="preview video"
                                className={styles.previewButton}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setModalVideoId(videoId);
                                  setModalOpen(true);
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>

                              {url.includes(videoId) && (
                                <span className={styles.selectedResultBadge}>Selected</span>
                              )}

                              <CardMedia
                                component="img"
                                image={thumbnail}
                                alt={title}
                                className={styles.videoThumb}
                              />

                              <CardContent sx={{ p: 1.2 }}>
                                <Typography className={styles.videoTitle}>
                                  {title.length > 70
                                    ? `${title.slice(0, 67)}...`
                                    : title}
                                </Typography>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>

                      {nextPageToken && (
                        <Box className={styles.loadMoreWrap}>
                          <Button
                            className={styles.secondaryActionButton}
                            variant="outlined"
                            onClick={handleLoadMore}
                            disabled={isLoading}
                          >
                            {isLoading ? "Loading..." : "Load More"}
                          </Button>
                        </Box>
                      )}
                    </div>
                  )}

                  {showResults && !isLoading && searchResults.length === 0 && (
                    <div className={styles.emptyState}>
                      No YouTube results found.
                    </div>
                  )}

                  <Modal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box className={styles.previewModalBox}>
                      {modalVideoId && (
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${modalVideoId}?autoplay=1&mute=0&controls=1`}
                          title="YouTube video player"
                          frameBorder="0"
                          allow="autoplay; encrypted-media"
                          allowFullScreen
                        />
                      )}
                    </Box>
                  </Modal>

                  <Field name="url">
                    {({ input, meta }) => (
                      <TextField
                        {...input}
                        label="https://youtube.com/watch?v=..."
                        fullWidth
                        required
                        size="small"
                        value={url}
                        onChange={(e) => {
                          setUrl(e.target.value);
                          input.onChange(e.target.value);
                        }}
                        onPaste={(e) => {
                          const pastedText = e.clipboardData.getData("text");
                          setTimeout(() => {
                            setUrl(pastedText);
                            input.onChange(pastedText);
                            handleScrubVideoDetails(pastedText, form);
                          }, 0);
                        }}
                        onBlur={() => {
                          if (input.value?.trim()) {
                            handleScrubVideoDetails(input.value.trim(), form);
                          }
                        }}
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                      />
                    )}
                  </Field>

                  {videoDetails.thumbnail && (
                    <img
                      src={videoDetails.thumbnail}
                      alt="Video Thumbnail"
                      className={styles.thumbnailPreview}
                    />
                  )}

                  {(url || videoDetails.title) && (
                    <div className={styles.detailsGrid}>
                      <Field name="title">
                        {({ input, meta }) => (
                          <TextField
                            {...input}
                            label="Video Title"
                            fullWidth
                            size="small"
                            value={input.value ?? ""}
                            onChange={(e) => {
                              input.onChange(e.target.value);
                              setFormValues((prev) => ({
                                ...prev,
                                title: e.target.value,
                              }));
                            }}
                            error={meta.touched && Boolean(meta.error)}
                            helperText={meta.touched && meta.error}
                          />
                        )}
                      </Field>

                      <Field name="description">
                        {({ input }) => (
                          <TextField
                            {...input}
                            label="Description"
                            fullWidth
                            multiline
                            rows={3}
                            value={input.value ?? ""}
                            onChange={(e) => {
                              input.onChange(e.target.value);
                              setFormValues((prev) => ({
                                ...prev,
                                description: e.target.value,
                              }));
                            }}
                          />
                        )}
                      </Field>
                    </div>
                  )}
                </div>
              )}

              {values.platform === "tk" && (
                <div className={styles.fieldCard}>
                  <Field name="tiktokCopyUrl">
                    {({ input, meta }) => (
                      <>
                        <TextField
                          {...input}
                          label="https://www.tiktok.com/..."
                          variant="outlined"
                          fullWidth
                          size="small"
                          value={input.value || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            input.onChange(value);
                            setUrl(value);
                            scheduleTikTokDetails(value, form);
                          }}
                          onPaste={(event) => {
                            event.preventDefault();
                            const pastedValue = event.clipboardData
                              .getData("text")
                              .trim();
                            input.onChange(pastedValue);
                            setUrl(pastedValue);
                            scheduleTikTokDetails(pastedValue, form);
                          }}
                          onBlur={(event) => {
                            input.onBlur(event);
                          }}
                          required
                          error={meta.touched && Boolean(meta.error)}
                          helperText={meta.touched && meta.error}
                        />

                        {(input.value || videoDetails.title || videoDetails.thumbnail) && (
                          <div className={styles.detailsGrid}>
                            <Field name="title">
                              {({ input: titleInput, meta: titleMeta }) => (
                                <TextField
                                  {...titleInput}
                                  label="Title"
                                  variant="outlined"
                                  value={titleInput.value || ""}
                                  size="small"
                                  fullWidth
                                  error={
                                    titleMeta.touched && Boolean(titleMeta.error)
                                  }
                                  helperText={
                                    titleMeta.touched && titleMeta.error
                                  }
                                  onChange={(e) => {
                                    titleInput.onChange(e.target.value);
                                    setFormValues((prev) => ({
                                      ...prev,
                                      title: e.target.value,
                                    }));
                                  }}
                                />
                              )}
                            </Field>

                            <Field name="description">
                              {({ input: descriptionInput }) => (
                                <TextField
                                  {...descriptionInput}
                                  label="Description"
                                  variant="outlined"
                                  value={descriptionInput.value || ""}
                                  size="small"
                                  fullWidth
                                  multiline
                                  rows={3}
                                  onChange={(e) => {
                                    descriptionInput.onChange(e.target.value);
                                    setFormValues((prev) => ({
                                      ...prev,
                                      description: e.target.value,
                                    }));
                                  }}
                                />
                              )}
                            </Field>

                            {videoDetails.thumbnail && (
                              <img
                                src={videoDetails.thumbnail}
                                alt="TikTok thumbnail"
                                className={styles.thumbnailPreview}
                              />
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </Field>
                </div>
              )}

              {values.platform === "S3" && (
                <div className={styles.stockPanel}>
                  <div className={styles.stockHeader}>
                    <div>
                      <h3 className={styles.stockTitle}>
                        Select videos to add to your playlist
                      </h3>
                      <p className={styles.stockSubtitle}>
                        Choose one or more stock videos from the library.
                      </p>
                    </div>

                    <span className={styles.stockCount}>
                      {selectedVideos.length} selected
                    </span>
                  </div>

                  {s3Videos.length > 0 ? (
                    <div className={styles.stockGrid}>
                      {s3Videos.map((video) => (
                        <Card
                          key={video.url}
                          className={`${styles.stockCard} ${
                            selectedVideos.includes(video.url)
                              ? styles.stockCardSelected
                              : ""
                          }`}
                          onMouseEnter={() => handleMouseEnter(video)}
                          onMouseLeave={handleMouseLeave}
                          onClick={() => toggleSelection(video.url)}
                        >
                          {hoveredVideo === video.url ? (
                            <video
                              src={video.url}
                              width="100%"
                              height="118"
                              autoPlay
                              muted
                              loop
                              className={styles.stockMedia}
                            />
                          ) : (
                            <CardMedia
                              component="img"
                              image={video.thumbnail || ""}
                              alt={video.title || ""}
                              className={styles.stockMedia}
                            />
                          )}

                          <CardContent sx={{ p: 1.2 }}>
                            <Typography className={styles.videoTitle} noWrap>
                              {video.title}
                            </Typography>
                          </CardContent>

                          {selectedVideos.includes(video.url) && (
                            <div className={styles.selectedBadge}>
                              <CheckCircleIcon fontSize="small" />
                            </div>
                          )}
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.emptyState}>No videos found.</div>
                  )}
                </div>
              )}

              <div className={styles.actions}>
                <Button
                  className={styles.primaryButton}
                  type="submit"
                  variant="contained"
                  disabled={
                    isLoading ||
                    isTikTokLoading ||
                    (values.platform === "S3" && selectedVideos.length === 0) ||
                    (values.platform === "youtube" &&
                      (!values.url?.trim() ||
                        !isValidYouTubeUrl(values.url.trim()) ||
                        !values.title?.trim() ||
                        hasDuplicateYouTubeVideo(
                          currentCategory?.videos || [],
                          values.url
                        ))) ||
                    (values.platform === "tk" &&
                      (!values.tiktokCopyUrl?.trim() ||
                        !isValidTikTokUrl(values.tiktokCopyUrl.trim()) ||
                        (currentCategory?.videos || []).some(
                          (video) =>
                            normalizeUrl(video.url) ===
                            normalizeUrl(values.tiktokCopyUrl)
                        )))
                  }
                  fullWidth
                >
                  {isTikTokLoading ? "Loading TikTok details..." : "+ Add Video"}
                </Button>
              </div>
            </form>
          )}
        />
      </DialogContent>
    </Dialog>
  );
};

export default PlaylistModal;
